import { BaseViewStore, DateTimeCellData, ProductActivityCellData } from 'core/stores';
import { action, makeObservable, _getAdministration } from 'mobx';
import { ProductActivityStore } from 'modules/product-activity/stores';
import ProductStore from './ProductStore';
import { ProductActivityForm } from 'modules/product/forms';
import { ApplicationStore } from 'platform/application/stores';
import { RemoveFiltersModalUtility, TableFilterUtility, QueryUtility } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import { LookupStore } from 'modules/lookup/stores';
import moment from 'moment';
import _ from 'lodash';

class ProductActivityViewStore extends BaseViewStore {
	data = [];
	noDataItem = true;
	product = null;
	form = new ProductActivityForm({
		onSuccess: async () => {
			await this.findProductActivity();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.initialize(rootStore);
	}

	@action.bound
	initialize = async rootStore => {
		this.suspend();
		this.defineTableData();
		this.listStore = new ProductStore(rootStore);
		this.productActivityStore = new ProductActivityStore(rootStore);
		this.columnFilterSetup = 'product-activity-table-data';
		this.lookupStore = new LookupStore(rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: new QueryUtility(this.rootStore, 'findProductHistoryLog'),
			columns: this.tableData.columns,
			services: [this.listStore.service],
		});
		await this.getProduct(rootStore.routerStore.routerState.params.id, {
			embed: ['productVirtualSkus'],
		});

		this.warehouseDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('warehouseId'),
			store: new ApplicationStore(rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				sellerWarehousesOnlyOrganizationId: this.product.sellerId,
				includeOnlySellerWarehouses: true,
			},
		});
		moment.locale(window.navigator.userLanguage || window.navigator.language);
		this.format = moment.localeData().longDateFormat('L');
		this.resume();
	};

	@action.bound
	getProduct = async (id, options) => {
		await this.listStore.getProduct(id, options);
		this.product = this.listStore.item;
	};

	@action.bound
	defineTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'dateCreated',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.DATE',
					dataCell: DateTimeCellData,
					dateFormat: 'L',
					useLocalTime: true,
					selected: true,
					sortable: false,
					width: 120,
					filter: null,
				},
				{
					key: 'dateCreated',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.TIME',
					dataCell: DateTimeCellData,
					dateFormat: 'hh:mm:ss a',
					useLocalTime: true,
					selected: true,
					sortable: false,
					width: 120,
					filter: null,
				},
				{
					key: 'quantityBefore',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.QUANTITYBEFORE',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 140,
					filter: null,
				},
				{
					key: 'quantityAfter',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.QUANTITYAFTER',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 130,
					filter: null,
				},
				{
					key: 'quantity',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.QUANTITY',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 100,
					filter: null,
				},
				{
					key: 'warehouseOnHoldQuantityBefore',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.WAREHOUSEONHOLDQUANTITYBEFORE',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 100,
					filter: null,
				},
				{
					key: 'warehouseOnHoldQuantityAfter',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.WAREHOUSEONHOLDQUANTITYAFTER',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 100,
					filter: null,
				},
				{
					key: 'warehouseOnHoldQuantity',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.WAREHOUSEONHOLDQUANTITY',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 100,
					filter: null,
				},
				{
					key: 'warehouse',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.WAREHOUSE',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 200,
					filter: null,
				},
				{
					key: 'warehouseActivityTypeName',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.ACTIVITYTYPE',
					selected: true,
					sortable: false,
					width: 250,
					filter: null,
				},
				{
					key: 'description',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.DESCRIPTION',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'receivingRef',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.RECEIVINGREF',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'receivingStatus',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.RECEIVINGSTATUS',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
				{
					key: 'orderNumber',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.ORDERNUMBER',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 200,
					filter: null,
				},
				{
					key: 'userName',
					title: 'PRODUCTACTIVITYVIEW.COLUMNS.USER',
					dataCell: ProductActivityCellData,
					selected: true,
					sortable: false,
					width: 150,
					filter: null,
				},
			],
		};
	};

	@action.bound
	findProductActivity = async () => {
		this.suspend();
		this.data = [];
		const { dateRangeStart, dateRangeEnd, warehouseId } = this.form.values();
		this.data = (await this.productActivityStore.find({
			productIds: [this.rootStore.routerStore.routerState.params.id],
			dateStart: moment(dateRangeStart, this.format).toISOString(),
			dateEnd: dateRangeEnd == '' ? null : moment(dateRangeEnd, this.format).toISOString(),
			warehouseId,
		})).item;
		this.resume();
	};
}

export default ProductActivityViewStore;
